// ABOUTME: Notification services for the quote system.
// ABOUTME: Mostly client-side (mailto/sms links), profile fetch now uses the API.

import { withErrorHandling } from '@/lib/errors';
import { Quote } from '@/lib/types';
import { ContactMethod, QuoteNotificationParams } from '@/lib/types/quote-types';
import { api } from "@/lib/supabase/client";
import { debug } from "@/lib/utils";
import { config } from "@/lib/config";

const NOTIFICATION_QUEUE_KEY = 'pendingQuoteNotifications';

export interface QueuedNotification {
  quote: Quote;
  contactMethod: ContactMethod;
  customMessage?: string;
  email?: string;
}

const loadNotificationQueue = (): QueuedNotification[] => {
  try {
    const storedQueue = localStorage.getItem(NOTIFICATION_QUEUE_KEY);
    return storedQueue ? JSON.parse(storedQueue) : [];
  } catch (error) {
    debug.error('Error loading notification queue from localStorage:', error);
    return [];
  }
}

let notificationQueue: QueuedNotification[] = loadNotificationQueue();

interface NotificationResult {
  success: boolean;
  method: ContactMethod;
  error?: Error;
}

export const addToNotificationQueue = (
  quote: Quote, contactMethod: ContactMethod = 'email',
  customMessage?: string, email?: string
): number => {
  notificationQueue.push({ quote, contactMethod, customMessage, email });
  try { localStorage.setItem(NOTIFICATION_QUEUE_KEY, JSON.stringify(notificationQueue)); } catch { /* localStorage may be unavailable */ }
  return notificationQueue.length;
};

export const getNotificationQueue = (): QueuedNotification[] => [...notificationQueue];

export const processNextNotification = async (): Promise<boolean> => {
  if (notificationQueue.length === 0) return false;
  const next = notificationQueue[0];
  try {
    const result = await sendQuoteRequestNotification(next.quote, next.contactMethod, next.customMessage, next.email);
    if (result.success) {
      notificationQueue.shift();
      localStorage.setItem(NOTIFICATION_QUEUE_KEY, JSON.stringify(notificationQueue));
      return true;
    }
    return false;
  } catch { return false; }
};

export const clearNotificationQueue = (): void => {
  notificationQueue = [];
  localStorage.removeItem(NOTIFICATION_QUEUE_KEY);
};

export const getNotificationQueueLength = (): number => notificationQueue.length;

export const sendQuoteRequestNotification = withErrorHandling(
  async (
    quoteOrParams: Quote | QuoteNotificationParams,
    contactMethodArg?: ContactMethod,
    customMessageArg?: string,
    emailArg?: string
  ): Promise<NotificationResult> => {
    let quote: Quote, contactMethod: ContactMethod, customMessage: string | undefined, email: string | undefined;

    if ('quote' in quoteOrParams) {
      quote = quoteOrParams.quote; contactMethod = quoteOrParams.contactMethod;
      customMessage = quoteOrParams.customMessage; email = quoteOrParams.email;
    } else {
      quote = quoteOrParams; contactMethod = contactMethodArg || 'email';
      customMessage = customMessageArg; email = emailArg;
    }

    const tradesperson = quote.tradesperson;
    if (!tradesperson) throw new Error('No tradesperson associated with quote');

    let userProfile: { email?: string; postcode?: string } | null = null;
    try {
      const res = await api.get<{ profile: { email?: string; postcode?: string } }>('/profiles/me');
      userProfile = res.profile;
    } catch { /* continue without profile */ }

    const useTestMode = config.features.useTestEmailForAll;
    const jobTitle = quote.job?.title || 'New job request';
    const jobDescription = quote.job?.description || 'No description provided';
    const subject = `Need help with ${jobTitle}`;
    const userSignature = email ? email.split('@')[0]
      : (userProfile?.email ? userProfile.email.split('@')[0] : 'A local customer');

    const body = customMessage || `
Hi there,

I'm looking for help with ${jobTitle.toLowerCase()} in ${userProfile?.postcode || '[postcode]'} and my neighbours recommended you.

${jobDescription.length > 150 ? jobDescription.substring(0, 150) + '...' : jobDescription}

Are you available to discuss this? When would suit you?

Thanks,
${userSignature}${userProfile?.postcode ? ` (${userProfile.postcode})` : ''}
    `.trim();

    const result: NotificationResult = { success: true, method: contactMethod };

    try {
      if (contactMethod === 'email') {
        const recipientEmail = useTestMode ? config.testEmail : tradesperson.email || config.testEmail;
        const mailtoUrl = `mailto:${recipientEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        const isMobileSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent);
        if (isMobileSafari) { window.open(mailtoUrl, '_self'); } else { window.open(mailtoUrl, '_blank'); }
      } else if (contactMethod === 'phone' || contactMethod === 'sms') {
        const recipientPhone = useTestMode ? config.testPhone : tradesperson.phone;
        if (!recipientPhone) {
          const fallbackEmail = useTestMode ? config.testEmail : (tradesperson.email || config.testEmail);
          window.open(`mailto:${fallbackEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
          result.method = 'email';
        } else {
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
          const smsUrl = isIOS ? `sms:${recipientPhone}&body=${encodeURIComponent(body)}` : `sms:${recipientPhone}?body=${encodeURIComponent(body)}`;
          window.open(smsUrl, isIOS ? '_self' : '_blank');
        }
      }
    } catch (error) {
      result.success = false;
      result.error = error instanceof Error ? error : new Error('Unknown error during notification');
    }

    return result;
  },
  { success: false, method: 'email' },
  'Failed to send quote notification'
);
